import React, { useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { Camera, Type, Settings, Video, Square, Download, RefreshCw, Info } from 'lucide-react';
import { createWorker, createScheduler } from 'tesseract.js';
import { useAppStore } from './store';
import { ImagePreview } from './components/ImagePreview';
import { TextOutput } from './components/TextOutput';
import { SettingsPanel } from './components/SettingsPanel';
import { ErrorPage } from './components/ErrorPage';
import { AboutPage } from './components/AboutPage';

function MainApp() {
  const {
    capturedImage,
    isProcessing,
    isRecording,
    ocrLanguage,
    setImage,
    setText,
    setProcessing,
    setSettingsOpen,
    setRecording,
    reset,
  } = useAppStore();

  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const handleReset = () => {
    reset();
    setRecordedChunks([]);
    setPermissionError(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const startScreenCapture = async () => {
    setPermissionError(null);
    setRecordedChunks([]);
    
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: { 
          cursor: "always" 
        },
        audio: true
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks(chunks => [...chunks, event.data]);
        }
      };

      mediaRecorder.start(1000);

      stream.getVideoTracks()[0].addEventListener('ended', () => {
        stopScreenCapture();
      });

      setRecording(true);
    } catch (error) {
      console.error('Error starting screen capture:', error);
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.message.includes('Permission denied')) {
          setPermissionError(
            'Screen capture permission was denied. To enable screen capture:\n' +
            '1. Click the camera icon in your browser\'s address bar\n' +
            '2. Select "Always allow" for this site\n' +
            '3. Try capturing again'
          );
        } else {
          setPermissionError('An unexpected error occurred while starting screen capture. Please try again.');
        }
      }
      setRecording(false);
    }
  };

  const stopScreenCapture = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setRecording(false);
    setPermissionError(null);
  };

  const saveRecording = () => {
    if (recordedChunks.length === 0) return;
    
    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screen-recording-${new Date().toISOString()}.webm`;
    a.click();

    URL.revokeObjectURL(url);
    setRecordedChunks([]);
  };

  const captureScreenshot = async () => {
    setPermissionError(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: { 
          cursor: "always" 
        },
        audio: false
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve(null);
        };
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/png');
      
      stream.getTracks().forEach(track => track.stop());
      
      setImage(imageData);
      setText('');
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.message.includes('Permission denied')) {
          setPermissionError(
            'Screenshot permission was denied. To enable screenshots:\n' +
            '1. Click the camera icon in your browser\'s address bar\n' +
            '2. Select "Always allow" for this site\n' +
            '3. Try capturing again'
          );
        } else {
          setPermissionError('An unexpected error occurred while capturing screenshot. Please try again.');
        }
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
        setText('');
      };
      reader.readAsDataURL(file);
    }
  };

  const preprocessImage = async (imageData: string): Promise<string> => {
    const response = await fetch(imageData);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    await new Promise((resolve) => {
      img.onload = resolve;
      img.src = imageData;
    });
    
    canvas.width = img.width;
    canvas.height = img.height;
    
    if (!ctx) throw new Error('Could not get canvas context');
    
    ctx.drawImage(img, 0, 0);
    
    const imageDataObj = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageDataObj.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      const contrast = 1.5;
      const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
      const enhancedGray = factor * (gray - 128) + 128;
      const threshold = 128;
      const binarized = enhancedGray > threshold ? 255 : 0;
      
      data[i] = binarized;
      data[i + 1] = binarized;
      data[i + 2] = binarized;
    }
    
    ctx.putImageData(imageDataObj, 0, 0);
    
    return canvas.toDataURL('image/png');
  };

  const processImage = async () => {
    if (!capturedImage) return;

    setProcessing(true);
    setText('');

    try {
      const scheduler = createScheduler();
      const worker = await createWorker(ocrLanguage);
      
      scheduler.addWorker(worker);

      const preprocessedImage = await preprocessImage(capturedImage);

      const workerParams = {
        tessedit_ocr_engine_mode: 1,
        preserve_interword_spaces: '1',
        textord_tabfind_vertical_text: '1',
        tessedit_pageseg_mode: '6',
        tessdata_dir: 'https://raw.githubusercontent.com/tesseract-ocr/tessdata_best/main',
        user_defined_dpi: '300',
        tessedit_char_blacklist: '{}[]',
        tessedit_enable_dict_correction: '1',
        tessedit_enable_bigram_correction: '1',
        textord_space_size_is_variable: '1',
      };

      await worker.setParameters(workerParams);

      const result = await scheduler.addJob('recognize', preprocessedImage);

      let processedText = result.data.text;
      
      if (ocrLanguage.includes('ara')) {
        processedText = processedText
          .split('\n')
          .filter(line => line.trim().length > 0)
          .map(line => {
            let processed = line.replace(/[\u0660-\u0669]/g, d => 
              String.fromCharCode(d.charCodeAt(0) - 0x0660 + 0x0030)
            );
            
            processed = processed.replace(/[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s\d.,:-]/g, '');
            
            processed = processed
              .replace(/[إأآا]/g, 'ا')
              .replace(/[ىي]/g, 'ي')
              .replace(/ة/g, 'ه')
              .replace(/\s+/g, ' ')
              .trim();
              
            return processed;
          })
          .filter(line => line.length > 0)
          .join('\n');
      } else {
        processedText = processedText
          .split('\n')
          .filter(line => line.trim().length > 0)
          .map(line => line.trim())
          .join('\n');
      }

      setText(processedText || 'No text was found in the image.');

      await scheduler.terminate();

    } catch (error) {
      console.error('OCR processing failed:', error);
      setText('Failed to process image. Please try again with a different image or check if the image contains clear text.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="max-w-6xl mx-auto p-6">
        <header className="flex items-center justify-between mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/capture/logo.svg" alt="Simiyah Logo" className="h-12 w-auto" />
              <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-transparent bg-clip-text">
                Simiyah
              </span>
            </div>
            <div className="pl-2 border-l-2 border-gray-200">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-transparent bg-clip-text">
                Screen Recording - Image to Text
              </h1>
              <p className="text-gray-600 mt-1">Capture, Process, Extract - All in One Place</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/about"
              className="p-3 rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-md"
              title="About"
            >
              <Info className="w-6 h-6 text-gray-600" />
            </Link>
            <button
              onClick={handleReset}
              className="p-3 rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-md"
              title="Reset"
            >
              <RefreshCw className="w-6 h-6 text-gray-600" />
            </button>
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-3 rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-md"
              title="Settings"
            >
              <Settings className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </header>

        <video 
          ref={videoRef} 
          autoPlay 
          className="hidden"
          onLoadedMetadata={() => {
            if (videoRef.current) {
              videoRef.current.play();
            }
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-lg font-medium text-gray-700">Capture Options</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={isRecording ? stopScreenCapture : startScreenCapture}
                  className={`flex items-center gap-2 px-4 py-2 ${
                    isRecording 
                      ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                      : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                  } text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md text-sm`}
                >
                  {isRecording ? <Square className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                  {isRecording ? 'Stop' : 'Record'}
                </button>
                {!isRecording && recordedChunks.length > 0 && (
                  <button
                    onClick={saveRecording}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Save
                  </button>
                )}
                <button
                  onClick={captureScreenshot}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md text-sm"
                >
                  <Camera className="w-4 h-4" />
                  Screenshot
                </button>
              </div>
            </div>
            
            {permissionError && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 whitespace-pre-line text-sm">{permissionError}</p>
              </div>
            )}
            
            <ImagePreview />

            <div className="mt-4 text-center">
              <p className="text-gray-500 text-sm mb-2">Or upload an image directly</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer text-sm">
                <Camera className="w-4 h-4" />
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-700">Extracted Text</h2>
              <button
                onClick={processImage}
                disabled={!capturedImage || isProcessing}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Type className="w-4 h-4" />
                {isProcessing ? 'Processing...' : 'Extract Text'}
              </button>
            </div>
            
            <TextOutput />
          </div>
        </div>
      </div>

      <footer className="mt-12 py-6 text-center text-sm">
        <p className="text-gray-600">
          A modern web application for image to text conversion
        </p>
        <p className="text-gray-500 mt-1">
          © <a href="https://altiti.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">altiti.com</a> 2025
        </p>
      </footer>

      <SettingsPanel />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter basename="/capture">
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;